import { ReactNode, useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import PagePreviewDrawer from '@rossum/components/page-preview/PagePreviewDrawer';
import {
  IconChevronDown,
  IconChevronUp,
  IconFileDescription,
  IconTrashX,
} from '@rossum/ui/icons/tabler';
import {
  Button,
  Chip,
  List,
  ListItem,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { dataDisabled } from '../../../redux/modules/annotation/helpers';
import { usePagePreview } from '../hooks/usePagePreview';
import { EmailAttachment } from './EmailAttachment';
import { AttachmentT } from './EmailAttachment/helpers';
import { getAttachmentRows, WithDepth } from './getAttachmentRows';

type Props = {
  attachments: 'loading' | AttachmentT[];
  shouldDisplayAttachmentError?: boolean;
  getSecondaryAction: (attachment: AttachmentT) => ReactNode;
};

export const DISPLAYED_ATTACHMENTS_COUNT = 3;

const shouldDisplayShowMoreLessButton = (
  attachments: 'loading' | WithDepth<AttachmentT>[]
) =>
  Array.isArray(attachments) &&
  attachments.length > DISPLAYED_ATTACHMENTS_COUNT;

const EmailAnnotationsPreview = ({
  attachments: rawAttachments,
  getSecondaryAction,
  shouldDisplayAttachmentError,
}: Props) => {
  const intl = useIntl();

  const [numberOfDisplayedAttachments, setNumberOfDisplayedAttachments] =
    useState(DISPLAYED_ATTACHMENTS_COUNT);

  const attachmentsLoading = rawAttachments === 'loading';

  const { attachmentsWithoutDuplicates, purgedAttachmentsCount } =
    useMemo(() => {
      if (rawAttachments === 'loading')
        return { attachmentsWithoutDuplicates: [], purgedAttachmentsCount: 0 };

      const attachmentsWithoutDuplicates = rawAttachments
        .filter(
          attachment =>
            attachment.document.attachmentStatus !==
            'filtered_by_thread_duplicate'
        )
        .filter(attachment =>
          attachment.annotation
            ? attachment.annotation.status !== 'purged'
            : true
        );

      const purgedAttachmentsCount = rawAttachments.filter(
        attachment =>
          attachment.annotation && attachment.annotation.status === 'purged'
      ).length;

      return { attachmentsWithoutDuplicates, purgedAttachmentsCount };
    }, [rawAttachments]);

  const attachmentsWithParentChildHierarchy = attachmentsLoading
    ? 'loading'
    : getAttachmentRows(attachmentsWithoutDuplicates);

  const [hoveredAttachment, setHoveredAttachment] = useState<
    AttachmentT | undefined
  >(undefined);

  const { data: pagePreviewData } = usePagePreview(
    hoveredAttachment?.annotation?.pages[0]
  );

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography
          variant="subtitle2"
          color="text.secondary"
          data-cy="attachements-list-title"
        >
          <Stack direction="row" component="span" spacing={2}>
            <SvgIcon fontSize="medium" color="inherit">
              <IconFileDescription />
            </SvgIcon>
            <span>
              {intl.formatMessage({
                id: 'components.emailDocumentsPreview.title',
              })}
            </span>
          </Stack>
        </Typography>
        <Chip
          data-cy={`attachments-count-${shouldDisplayAttachmentError ? 'error' : 'default'}`}
          color={shouldDisplayAttachmentError ? 'error' : 'default'}
          label={attachmentsWithoutDuplicates.length}
        />
      </Stack>
      {attachmentsWithParentChildHierarchy === 'loading' ? (
        <Stack spacing={0}>
          {[1, 2, 3].map(n => (
            <Skeleton key={n} height={40} width="100%" sx={{ mt: 0 }} />
          ))}
        </Stack>
      ) : Array.isArray(attachmentsWithParentChildHierarchy) &&
        attachmentsWithParentChildHierarchy.length ? (
        <List dense data-cy="attachements-list">
          {R.take(
            attachmentsWithParentChildHierarchy,
            numberOfDisplayedAttachments
          ).map(attachment => (
            <EmailAttachment
              attachment={attachment}
              key={attachment.item.document.url}
              secondaryAction={getSecondaryAction(attachment.item)}
              onMouseEnter={attachmentToHover => {
                setHoveredAttachment(attachmentToHover);
              }}
              onMouseLeave={() => setHoveredAttachment(undefined)}
            />
          ))}
          {purgedAttachmentsCount > 0 ? (
            <ListItem
              sx={{
                backgroundColor: t => t.palette.action.disabledBackground,
              }}
            >
              <Stack direction="row" spacing={1} useFlexGap>
                <SvgIcon fontSize="medium" sx={{ color: 'text.disabled' }}>
                  <IconTrashX />
                </SvgIcon>
                <Typography variant="body2" color="text.disabled">
                  {intl.formatMessage({
                    id: 'components.emailDocumentsPreview.hasPurgedAnnotations',
                  })}
                </Typography>
              </Stack>
            </ListItem>
          ) : null}
          {hoveredAttachment &&
          hoveredAttachment.annotation &&
          !dataDisabled.includes(hoveredAttachment.annotation.status) ? (
            <PagePreviewDrawer
              isNested
              originalFileName={
                hoveredAttachment.document.originalFileName ?? ''
              }
              pageCount={hoveredAttachment.annotation.pages.length}
              previewImage={pagePreviewData}
            />
          ) : null}
        </List>
      ) : (
        <Typography variant="body2" color="text.disabled">
          {intl.formatMessage({
            id:
              purgedAttachmentsCount !== 0 &&
              purgedAttachmentsCount === rawAttachments.length
                ? 'components.emailDocumentsPreview.allAnnotationsWerePurged'
                : 'components.emailDocumentsPreview.noDocuments',
          })}
        </Typography>
      )}
      {shouldDisplayShowMoreLessButton(attachmentsWithParentChildHierarchy) && (
        <Button
          color="secondary"
          onClick={() =>
            setNumberOfDisplayedAttachments(
              attachmentsWithParentChildHierarchy.length !==
                numberOfDisplayedAttachments
                ? attachmentsWithParentChildHierarchy.length
                : DISPLAYED_ATTACHMENTS_COUNT
            )
          }
          startIcon={
            <SvgIcon fontSize="large">
              {attachmentsWithParentChildHierarchy.length !==
              numberOfDisplayedAttachments ? (
                <IconChevronDown />
              ) : (
                <IconChevronUp />
              )}
            </SvgIcon>
          }
          size="small"
          sx={{ alignSelf: 'flex-start', ml: 4 }}
          data-cy={
            attachmentsWithParentChildHierarchy.length !==
            numberOfDisplayedAttachments
              ? 'show-more-attachments'
              : 'show-less-attachments'
          }
        >
          {intl.formatMessage({
            id:
              attachmentsWithParentChildHierarchy.length !==
              numberOfDisplayedAttachments
                ? 'components.emailDocumentsPreview.button.showAll'
                : 'components.emailDocumentsPreview.button.showLess',
          })}
        </Button>
      )}
    </Stack>
  );
};

export { EmailAnnotationsPreview };
